'use client'

import { useEffect, useRef, useState } from 'react'

interface CountUpProps {
    end: number
    duration?: number
    delay?: number
    suffix?: string
}

export const CountUp = ({ end, duration = 2000, delay = 0, suffix = '' }: CountUpProps) => {
    const [count, setCount] = useState(0)
    const [isVisible, setIsVisible] = useState(false)
    const [hasAnimated, setHasAnimated] = useState(false)
    const [isMounted, setIsMounted] = useState(false)
    const elementRef = useRef<HTMLSpanElement>(null)

    useEffect(() => {
        setIsMounted(true)
    }, [])

    useEffect(() => {
        if (!isMounted) return

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated) {
                    setIsVisible(true)
                    setHasAnimated(true)
                }
            },
            { threshold: 0.1 }
        )

        if (elementRef.current) {
            observer.observe(elementRef.current)
        }

        return () => observer.disconnect()
    }, [hasAnimated, isMounted])

    useEffect(() => {
        if (!isVisible || !isMounted) return

        const timer = setTimeout(() => {
            const startTime = Date.now()

            const updateCount = () => {
                const elapsed = Date.now() - startTime
                const progress = Math.min(elapsed / duration, 1)

                const easeOut = 1 - Math.pow(1 - progress, 3)
                const currentCount = Math.floor(easeOut * end)

                setCount(currentCount)

                if (progress < 1) {
                    requestAnimationFrame(updateCount)
                } else {
                    setCount(end)
                }
            }

            requestAnimationFrame(updateCount)
        }, delay)

        return () => clearTimeout(timer)
    }, [isVisible, end, duration, delay, isMounted])

    if (!isMounted) {
        return (
            <span ref={elementRef}>
                {end.toLocaleString()}{suffix}
            </span>
        )
    }

    return (
        <span ref={elementRef}>
            {count.toLocaleString()}{suffix}
        </span>
    )
}
